import { Form, FormikProps } from "formik";
import { FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import LoadingProps from "@src/common/helpers/LoadingProps";
import { useIsMobile } from "@src/common/styles/media";
import { RegisterModel } from "@src/modules/Public/Register/useRegister";
import {
  PublicFormButtons,
  PublicFormCheckbox,
  PublicFormHeader,
  PublicFormLabel,
} from "@src/modules/Public/shared/components";
import RulesAndPrivacy from "@src/modules/Public/shared/RulesAndPrivacy";
import routesPaths from "@src/modules/Routing/routesPaths";
import Button from "@src/modules/shared/components/Button";
import CheckboxField from "@src/modules/shared/components/Form/Checkbox/CheckboxField";
import FormGroup from "@src/modules/shared/components/Form/FormGroup";
import { InputField } from "@src/modules/shared/components/Form/Input";
import Label from "@src/modules/shared/components/Form/Label";
import { PasswordInputField } from "@src/modules/shared/components/Form/PasswordInput";

type Props = FormikProps<RegisterModel> & LoadingProps;

const RegisterFormContent: FunctionComponent<Props> = ({
  isSubmitting,
  isLoading,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const submitButton = useMemo(
    () => (
      <Button
        type="submit"
        buttonType="primary"
        size="large"
        disabled={isSubmitting || isLoading}
      >
        {t("Register")}
      </Button>
    ),
    [isLoading, isSubmitting, t],
  );

  const goToLoginPage = useCallback(() => {
    history.push(routesPaths.LOGIN);
  }, [history]);

  return (
    <Form>
      <PublicFormHeader>{t("Register")}</PublicFormHeader>
      <FormGroup>
        <Label>{t("First and last name")}</Label>
        <InputField
          name="userName"
          placeholder={t("Enter names")}
          showError={true}
          disabled={isLoading}
          autoComplete="new-password"
        />
      </FormGroup>
      <FormGroup>
        <Label>{t("E-mail")}</Label>
        <InputField
          name="email"
          type="email"
          placeholder={t("Enter e-mail")}
          showError={true}
          disabled={isLoading}
          autoComplete="new-password"
        />
      </FormGroup>
      <FormGroup>
        <Label>{t("Password")}</Label>
        <PasswordInputField
          name="password"
          placeholder={t("Enter password")}
          showError={true}
          disabled={isLoading}
          autoComplete="new-password"
        />
      </FormGroup>
      <PublicFormCheckbox>
        <CheckboxField name="policyChecked" showError={true}>
          <RulesAndPrivacy />
        </CheckboxField>
      </PublicFormCheckbox>
      <PublicFormButtons>
        {!isMobile && submitButton}
        <PublicFormLabel>{t("Already using Diagnomatic?")}</PublicFormLabel>
        <Button buttonType="secondary" size="large" onClick={goToLoginPage}>
          {t("Log in")}
        </Button>
        {isMobile && submitButton}
      </PublicFormButtons>
    </Form>
  );
};

export default RegisterFormContent;
